import { NgClass } from '@angular/common';
import { Component, inject, OnInit, output, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { BOOKING_PAGE_VIEWS, GTM_EVENTS, WIDGET_TEMPLATES_CONST, WORKER_ATTRIBUTE_TYPES, WORKERS_VIEW } from '@constants/app.constants';
import { TEMPLATE_CONSTANT } from '@constants/template.constants';
import { environment } from '@environments/environment';
import { AppointmentServiceModel, ServiceWorkerModel } from '@models/appointment-service.model';
import { CurrentViewData } from '@models/widget-conf.model';
import { StoreDbModel } from '@models/store-db.model';
import { WidgetTemplateModel } from '@models/widget-template.model';
import { WorkerDbModel } from '@models/worker-db.model';
import { NgSelectModule } from '@ng-select/ng-select';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { AddressFormatPipe } from '@pipes/address-format.pipe';
import { CalioMeetingTemplatePipe } from '@pipes/calio-meeting-template.pipe';
import { CalioSafePipe } from '@pipes/calio-safe.pipe';
import { CalioTranslationPipe } from '@pipes/calio-translation.pipe';
import { TrustHtmlPipe } from '@pipes/trust-html.pipe';
import { BookingService } from '@services/feature/booking.service';
import { WidgetService } from '@services/feature/widget.service';
import { GoogleAnalyticsService } from '@services/utils/google-analytics.service';
import { HelperService } from '@services/utils/helper.service';
import { LoggerService } from '@services/utils/logger.service';
import { workerServesStore, workerServesStoreUuid } from '@services/utils/store-scope.util';
import { SwRouteService } from '@services/utils/sw-route.service';
import { ButtonComponent } from '@ui-lib/buttons/button/button.component';
import { InfoCardComponent } from '@ui-lib/cards/info-card/info-card.component';
import { CswTextComponent } from '@ui-lib/typography/csw-text/csw-text.component';

const modules = [FormsModule, NgSelectModule, TranslateModule];
const components = [CswTextComponent, InfoCardComponent, ButtonComponent];
const pipes = [CalioTranslationPipe, CalioSafePipe, CalioMeetingTemplatePipe, TrustHtmlPipe, AddressFormatPipe];

@Component({
  selector: 'app-workers-selection',
  templateUrl: './workers-selection.component.html',
  styleUrls: ['./workers-selection.component.scss'],
  imports: [NgClass, ...components, ...modules, ...pipes]
  // ChangeDetectionStrategy.OnPush deliberately NOT added here (see the implementer's
  // dev-notes for the full reasoning): the constructor's translate.onLangChange
  // subscription mutates the plain `lang` field outside any template event, and no
  // sibling booking component in this codebase yet combines OnPush with that same
  // subscription pattern to verify it live-updates correctly. workers/filteredWorkers
  // ARE converted to signal() below (safe under either strategy) per the scoped
  // instruction.
})
export class WorkersSelectionComponent implements OnInit {

  protected readonly workerAvatarBaseUrl = environment.workerAvatarBaseUrl;
  protected readonly baseUrl = environment.imageHostName;
  protected readonly storeBaseUrl = environment.storeBaseUrl;
  protected readonly workersViews = WORKERS_VIEW;
  protected readonly workerAttributeTypes = WORKER_ATTRIBUTE_TYPES;
  protected readonly templateConstant = TEMPLATE_CONSTANT;

  protected workers = signal<WorkerDbModel[]>([]);
  protected filteredWorkers = signal<WorkerDbModel[]>([]);
  protected selectedWorkerId: number;
  private selectedWorker: WorkerDbModel;
  protected lang: string;
  protected widgetAnyoneLabelTemplate: WidgetTemplateModel;
  protected widgetAnyoneDescTemplate: WidgetTemplateModel;
  protected widgetResourceHeadingTemplate: WidgetTemplateModel;
  private isGTMEventSent: boolean;
  protected widgetShowResourceStoreName = 0;
  private contextStoreUuids: string[] = [];
  protected workersView: string;
  protected allowMultipleNavigation = true;
  protected showWorkerSelectionRequiredError = false;

  nextPageEvent = output<CurrentViewData>();
  previousPageEvent = output<CurrentViewData>();

  private translate = inject(TranslateService);
  private widgetService = inject(WidgetService);
  private bookingService = inject(BookingService);
  private swRouteService = inject(SwRouteService);
  private googleAnalyticsService = inject(GoogleAnalyticsService);
  private helperService = inject(HelperService);

  constructor() {
    this.translate.onLangChange.subscribe(language => this.lang = language.lang);
  }

  // Display-only: prefer the store the customer selected over the worker's primary store
  // (a multi-store worker's primary can differ from the selected one). Falls back to the
  // worker's own store when no real store is selected — single-store, no-selection and the
  // "anyone store" (id 0) paths render exactly as before. In the multi-store deep-link
  // context the list spans several stores, so each card keeps its own store.
  protected displayStore(worker: WorkerDbModel): StoreDbModel {
    if (this.contextStoreUuids?.length) {
      return worker?.store;
    }
    // Only show the selected store on a card when that worker actually serves it — the worker
    // list is not always store-filtered (e.g. a non-store meeting type skips the filter), and
    // an unguarded preference would label every card with the pinned store.
    const selected = this.bookingService.selectedBookingData.currentSelectedAppointmentState.selectedStore;
    return selected?.id && workerServesStore(worker, selected.id) ? selected : worker?.store;
  }

  ngOnInit(): void {
    if (this.bookingService.selectedBookingData.currentSelectedAppointmentState.selectedStoreUuids?.length) {
      this.contextStoreUuids = this.bookingService.selectedBookingData.currentSelectedAppointmentState.selectedStoreUuids;
    }

    this.widgetShowResourceStoreName = Number(this.bookingService.partnerData?.widget_show_resource_store_name);
    let checkForAutoNavigation = false;
    this.isGTMEventSent = false;
    this.lang = this.translate.getFallbackLang();
    this.bookingService.loadAppointmentStateFromLocalStorage();

    setTimeout(() => {
      // [hidden_booking_steps] WORKERS hidden → force worker = 0 (Anyone) and skip in the travel
      // direction. Worker 0 needs no worker list, so we resolve it before findWorkers(). On backward
      // navigation the value is already persisted, so just continue the back-chain via previous().
      if (this.bookingService.isStepHidden(BOOKING_PAGE_VIEWS.WORKERS_VIEW)) {
        if (this.bookingService.isNavigatingBackTo(BOOKING_PAGE_VIEWS.WORKERS_VIEW)) {
          this.previous();
          return;
        }
        const context = this.widgetService.widgetConf?.context;
        this.selectedWorkerId = 0;
        // onWorkerChange already advances to SLOTS when one-page / auto-step-forward is on;
        // otherwise advance explicitly. Never do both (would double-navigate).
        this.onWorkerChange(false);
        if (!(context?.enable_one_page_ui || context?.auto_step_forward_on_select)) {
          this.next(false);
        }
        return;
      }

      this.findWorkers();

      if (
        this.bookingService?.selectedBookingData?.currentSelectedAppointmentState?.selectedWorkerId ||
        this.bookingService?.selectedBookingData?.currentSelectedAppointmentState?.selectedWorkerId === 0
      ) {
        checkForAutoNavigation = true;
        this.selectedWorkerId = this.bookingService?.selectedBookingData?.currentSelectedAppointmentState?.selectedWorkerId;
        if (!this.bookingService?.selectedBookingData?.currentSelectedAppointmentState?.selectedWorker) {
          this.onWorkerChange(false);
        } else {
          this.selectedWorker = this.bookingService?.selectedBookingData?.currentSelectedAppointmentState?.selectedWorker;
        }
      } else {
        const contextWorkerId = this.widgetService.widgetConf?.context?.worker_id;
        const contextWorkerIdStr = String(contextWorkerId);
        if (contextWorkerId != null && contextWorkerIdStr !== 'null' && contextWorkerIdStr !== 'undefined') {
          const worker = this.filteredWorkers().find(worker => worker.id === Number(contextWorkerId));
          if (worker) {
            this.selectedWorkerId = worker.id;
            this.onWorkerChange(false);
            checkForAutoNavigation = true;
          }
        } else if (this.widgetService.widgetConf?.context?.worker_uuid) {
          const worker = this.workers().find(worker => worker.uuid === this.widgetService.widgetConf?.context?.worker_uuid);
          if (worker) {
            this.selectedWorkerId = worker.id;
            this.onWorkerChange(false);
            checkForAutoNavigation = true;
          }
        } else if (this.widgetService.widgetConf?.context?.worker_email) {
          const worker = this.workers().find(worker => worker.email === this.widgetService.widgetConf?.context?.worker_email);
          if (worker) {
            this.selectedWorkerId = worker.id;
            this.onWorkerChange(false);
            checkForAutoNavigation = true;
          }
        } else if (
          this.widgetService?.widgetConf?.context?.select_any_worker_for_selected_meeting_type?.includes(this.bookingService.selectedBookingData.currentSelectedAppointmentState.selectedMeetingTypeId) &&
          this.filteredWorkers()[0]?.id === 0
        ) {
          this.selectedWorkerId = 0;
          this.onWorkerChange(false);
          this.next(false);
          return;
        }
      }

      if (
        checkForAutoNavigation === true &&
        this.allowMultipleNavigation &&
        (
          this.swRouteService.isAutoSkipStepsEnabled() ||
          // An advisor pre-selected via the worker-select booking question is an explicit choice —
          // the worker step is redundant and must be skipped even when auto_skip_steps is off.
          this.bookingService.selectedBookingData.currentSelectedAppointmentState.storeAndWorkerPreSelectedThroughWorkerSelectField
        )
      ) {
        if (this.bookingService.lastView === BOOKING_PAGE_VIEWS.SLOTS_VIEW) {
          if (this.workers()?.length === 1 || (this.widgetService.widgetConf.context?.worker_id === this.selectedWorkerId)) {
            this.previous();
          }
        } else {
          this.onWorkerChange(false);
          this.next(false);
        }
      }
    }, 200);

    this.setupTemplates();
  }

  findWorkers(): void {
    const currentSelectedServices = this.bookingService.selectedBookingData.selectedServices.filter(item => {
      return this.bookingService.selectedBookingData.currentSelectedAppointmentState.selectedServiceIds.indexOf(item.id) > -1;
    });

    // Round Robin V2 selection-page visibility (ADR-026): a worker flagged is_hidden_by_priority_group
    // for any selected service is excluded unless deep-linked / pre-selected. The BE flags per
    // service, so the basket-wide decision is made in collectServiceWorkers. Rows are never
    // removed server-side, so a service whose workers are ALL hidden still reaches this component.
    // Fail-safe: when the visible list is empty and no "Anyone" option will be offered, hiding
    // would turn into blocking (nothing selectable, no way forward) — fall back to the unfiltered
    // list. "Anyone" is not offered when egal_wer_show is off or when a worker_ids deep link
    // restricts the pool (see below), so both cases take the fallback.
    const contextWorkerIds = this.widgetService.widgetConf?.context?.worker_ids;
    const hasWorkerIdsContext = contextWorkerIds?.length > 0;
    let serviceWorkers = this.collectServiceWorkers(currentSelectedServices, true);
    if (!serviceWorkers.length && (this.widgetService?.widgetConf?.partner?.egal_wer_show !== 1 || hasWorkerIdsContext)) {
      const unfiltered = this.collectServiceWorkers(currentSelectedServices, false);
      if (unfiltered.length) {
        LoggerService.warn('[priority groups] Every worker hidden and no "Anyone" option — showing the unfiltered list');
        serviceWorkers = unfiltered;
      }
    }

    // define view of workers, in case it is hardcoded from context using `workers_view` (This has been updated for AMAG)
    // Also decided when the list is empty but "Anyone" will be offered — the view gates the template.
    if (serviceWorkers?.length || this.widgetService?.widgetConf?.partner?.egal_wer_show === 1) {
      if ([this.workersViews.CARD, this.workersViews.DROPDOWN].includes(this.widgetService.widgetConf?.context?.workers_view)) {
        this.workersView = this.widgetService.widgetConf.context.workers_view;
      } else if (serviceWorkers?.length <= 10) {
        this.workersView = this.workersViews.CARD;
      } else if (serviceWorkers?.length > 10) {
        this.workersView = this.workersViews.DROPDOWN;
      }
    }

    const sortedWorkers = this.helperService.sortBy(serviceWorkers, 'resource_name');
    this.workers.set(sortedWorkers);
    this.filteredWorkers.set(sortedWorkers);

    // When `worker_ids` is supplied via context (deep-link to a fixed worker pool that may span
    // multiple stores), restrict the list to those IDs and suppress the "Anyone" pseudo-worker
    // since it would defeat the deep-link. Silent fallback if the intersection is empty.
    if (hasWorkerIdsContext) {
      const filtered = this.workers().filter(worker => contextWorkerIds.includes(worker.id));
      if (filtered.length) {
        LoggerService.warn('[worker_ids] Restricting worker list to deep-linked workers:', contextWorkerIds);
        this.workers.set(filtered);
        this.filteredWorkers.set(filtered);
      } else {
        LoggerService.warn('[worker_ids] Worker intersection empty — falling back to unrestricted list');
      }
    }

    // When length of storeDefaultWorkerIds is > 0 means, store has multiple mapped workers which needs to be filtered out first
    // before moving further. The dropdown view (`filteredWorkers`) follows that narrowed set only for partners with the
    // postcode feature, where the card view and the dropdown must offer the same mapped resources; every other partner
    // keeps the dropdown it had.
    const postcodeFeatureEnabled = Number(this.widgetService?.widgetConf?.partner?.enable_store_postcode_feature) === 1;
    const workersBeforeStoreNarrowing = this.workers();
    if (this.bookingService.selectedBookingData.currentSelectedAppointmentState.storeDefaultWorkerIds?.length) {
      const filteredWorker = this.workers().filter(worker => this.bookingService.selectedBookingData.currentSelectedAppointmentState.storeDefaultWorkerIds.includes(worker.id));
      if (filteredWorker?.length) {
        LoggerService.warn('[Default store worker]: Default store workers are present so overwriting all the supported workers by default worker');
        this.workers.set(filteredWorker);
        if (postcodeFeatureEnabled) {
          this.filteredWorkers.set(filteredWorker);
        }
      }
    }

    if (!hasWorkerIdsContext && this.widgetService?.widgetConf?.partner?.egal_wer_show === 1) {
      const anyone = new WorkerDbModel();
      anyone.id = 0;
      this.filteredWorkers.set([anyone, ...(postcodeFeatureEnabled ? this.workers() : workersBeforeStoreNarrowing)]);
    }

    // When is_widget_postcode_worker_random_selection_enabled setting is enable and default worker from postcode
    // is null then worker selection will be random and selection will be skipped
    if (Number(this.bookingService.partnerData.is_widget_postcode_worker_random_selection_enabled) === 1) {
      // If default worker from postcode is null then worker selection will be random and selection will be skipped
      // else case is to check if user is coming back from slot step to worker then we should skip it as
      // is_widget_postcode_worker_random_selection_enabled setting is enabled

      if (
        this.bookingService.selectedBookingData.currentSelectedAppointmentState.storeDefaultWorkerIds ||
        !this.bookingService.selectedBookingData.currentSelectedAppointmentState.selectedWorkerId
      ) {
        if (this.bookingService.lastView === BOOKING_PAGE_VIEWS.SLOTS_VIEW) {
          this.allowMultipleNavigation = false;
          this.previous();
          return;
        }

        const randomIndex = Math.floor(Math.random() * this.workers().length);
        this.selectedWorkerId = this.workers()[randomIndex]?.id ?? 0;
        this.onWorkerChange(false);
        if (this.swRouteService.isAutoSkipStepsEnabled()) {
          if (this.bookingService.lastView === BOOKING_PAGE_VIEWS.SLOTS_VIEW) {
            this.previous();
          } else {
            this.next(false);
          }
        }
        return;
      } else if (
        this.bookingService?.selectedBookingData?.currentSelectedAppointmentState?.selectedWorkerId &&
        this.bookingService.lastView === BOOKING_PAGE_VIEWS.SLOTS_VIEW
      ) {
        if (Number(this.bookingService.partnerData.is_widget_postcode_worker_random_selection_enabled) === 1) {
          this.allowMultipleNavigation = false;
          this.previous();
        }
        return;
      }
    } else {
      if (this.workers()?.length === 1) {
        if (!this.bookingService?.selectedBookingData?.currentSelectedAppointmentState?.selectedWorkerId) {
          this.selectedWorkerId = this.workers()[0].id;
          this.onWorkerChange(false);
          if (this.swRouteService.isAutoSkipStepsEnabled()) {
            if (this.bookingService.lastView === BOOKING_PAGE_VIEWS.SLOTS_VIEW) {
              this.previous();
            } else {
              this.next(false);
            }
          }
          return;
        }
      }
    }
  }

  getUniqueListBy(arr, key): any {
    return [...new Map(arr.map(item => [item[key], item])).values()];
  }


  /**
   * Builds the bookable worker list for the selected services. With `applyVisibility` the
   * Round Robin V2 selection-page flag is honoured (see isServiceWorkerVisible) for the whole
   * basket: a worker hidden for ANY selected service is left out, even when another selected
   * service lists them unflagged. Without it every row is taken — used as the fail-safe when
   * hiding would otherwise block the step.
   */
  private collectServiceWorkers(services: AppointmentServiceModel[], applyVisibility: boolean): WorkerDbModel[] {
    let serviceWorkers: WorkerDbModel[] = [];
    const hiddenWorkerIds = new Set<number>(
      applyVisibility
        ? services.flatMap(service => (service.services_workers ?? [])
          .filter((item: ServiceWorkerModel) => !this.isServiceWorkerVisible(item))
          .map((item: ServiceWorkerModel) => item.worker_id))
        : []
    );

    for (const selectedService of services) {
      if (selectedService.services_workers?.length > 0) {
        const visibleServiceWorkers = selectedService.services_workers.filter(
          (item: ServiceWorkerModel) => !hiddenWorkerIds.has(item.worker_id)
        );

        serviceWorkers = serviceWorkers.concat(visibleServiceWorkers.map((item: ServiceWorkerModel) => item.worker));
        if (
          this.bookingService.isMeetingTypeStepInFlow() &&
          this.bookingService?.selectedBookingData?.currentSelectedAppointmentState?.selectedMeetingTypeId !== 1
        ) {
        } else {
          if (
            this.bookingService.selectedBookingData.currentSelectedAppointmentState.selectedStoreId &&
            this.bookingService.selectedBookingData.currentSelectedAppointmentState.selectedStoreId !== 0
          ) {
            serviceWorkers = serviceWorkers.filter(item => {
              // if -> when store_uuids are present in context then show all the workers are from mentioned stores (This has been updated for AMAG)
              // else -> show worker for selected store only
              if (this.contextStoreUuids?.length) {
                // A worker with an empty stores[] must fall back to its primary store —
                // `item.stores?.some()` returns `false` (not null), so `??` never caught it.
                // workerServesStoreUuid() encodes that fallback (length ? some : primary).
                return item?.id && this.contextStoreUuids.some(u => workerServesStoreUuid(item, u));
              } else {
                return item?.id && workerServesStore(item, this.bookingService.selectedBookingData.currentSelectedAppointmentState.selectedStoreId);
              }
            });
          }
        }

        serviceWorkers = serviceWorkers?.filter(item => Number(item?.bookable) === 1 && item.is_directly_bookable === true);
        serviceWorkers = this.getUniqueListBy(serviceWorkers, 'id');
      }
    }

    return serviceWorkers;
  }

  /**
   * Round Robin V2 selection-page visibility (ADR-026). A row not flagged by the BE is always
   * visible. A flagged row is still visible when it is the explicit target of a deep link or of
   * an advisor pre-selected through the worker-select booking question — "hidden" narrows the
   * pick list, it never blocks an already-decided booking.
   */
  private isServiceWorkerVisible(serviceWorker: ServiceWorkerModel): boolean {
    if (!serviceWorker.is_hidden_by_priority_group) {
      return true;
    }

    const context = this.widgetService.widgetConf?.context;
    const state = this.bookingService.selectedBookingData.currentSelectedAppointmentState;

    return (
      (context?.worker_id != null && Number(context.worker_id) === serviceWorker.worker_id) ||
      (context?.worker_ids?.length > 0 && context.worker_ids.includes(serviceWorker.worker_id)) ||
      (!!context?.worker_uuid && serviceWorker.worker?.uuid === context.worker_uuid) ||
      (!!context?.worker_email && serviceWorker.worker?.email === context.worker_email) ||
      (state?.storeAndWorkerPreSelectedThroughWorkerSelectField && state.selectedWorkerId === serviceWorker.worker_id)
    );
  }

  onWorkerChange(isUserInteracted = true): void {
    // Reset Error
    this.showWorkerSelectionRequiredError = false;

    if (isUserInteracted) {
      this.googleAnalyticsService.triggerApoointmentStartEvent();

      if (!this.isGTMEventSent) {
        this.googleAnalyticsService.emitAppointmentBookingEvent(GTM_EVENTS.appointment_booking_step_resources_start);
        this.isGTMEventSent = true;
      }
    }
    if (this.selectedWorkerId === 0) {
      this.selectedWorker = new WorkerDbModel();
      this.selectedWorker.id = 0;
    } else {
      this.selectedWorker = this.workers().find((item: WorkerDbModel) => {
        return item.id === this.selectedWorkerId;
      });
    }

    if (
      this.widgetService.widgetConf?.context?.enable_one_page_ui ||
      this.widgetService.widgetConf?.context?.auto_step_forward_on_select
    ) {
      // Proceeds to the next step if the one-page UI is enabled.
      // Or if auto step forward on select is enabled.
      // isUserInteracted stays true so the GTM event and the scroll-into-view are unchanged; only
      // the validation message is withheld when this ran on entry rather than on a real pick.
      this.next(true, isUserInteracted);
    }
  }

  /**
   * @param showValidation surface the "pick someone" error when the selection cannot be resolved.
   *   Separate from isUserInteracted on purpose: onWorkerChange()'s one-page / auto-step-forward
   *   advance calls next() with isUserInteracted defaulted to true (it drives the GTM event and
   *   the scroll-into-view, which that path does want), but it also runs on step ENTRY - and a
   *   validation error the customer has not yet had a chance to cause is noise, announced through
   *   an aria-live="assertive" region.
   */
  next(isUserInteracted = true, showValidation = isUserInteracted): void {
    // Worker is is not selected and clicked on next then show error.
    if (this.selectedWorkerId === null || this.selectedWorkerId === undefined) {
      this.showWorkerSelectionRequiredError = showValidation;
      return;
    }

    if (this.selectedWorker) {
      this.bookingService.selectedBookingData.currentSelectedAppointmentState.selectedWorker = this.selectedWorker;
      this.bookingService.selectedBookingData.currentSelectedAppointmentState.selectedWorkerId = this.selectedWorker.id;
      this.bookingService.setAppointmentStateInLocalStorage(this.bookingService.selectedBookingData.currentSelectedAppointmentState);

      // set store_id on worker select when store_uuids are present in context(This has been updated for AMAG)
      // only snap to the worker's primary store when the already-selected store is not one they serve —
      // a multi-store worker keeps a valid selected store instead of being pushed back to their primary.
      if (this.contextStoreUuids?.length) {
        const selectedStoreId = this.bookingService.selectedBookingData.currentSelectedAppointmentState.selectedStoreId;
        if (!workerServesStore(this.selectedWorker, selectedStoreId)) {
          this.bookingService.selectedBookingData.currentSelectedAppointmentState.selectedStoreId = this.selectedWorker.store_id;
          // Keep the object in sync with the snapped id, otherwise the slot page can still
          // display the previous store while slots/booking run against the primary one.
          this.bookingService.selectedBookingData.currentSelectedAppointmentState.selectedStore = this.selectedWorker.store;
        }
      }

      if (isUserInteracted) {
        this.googleAnalyticsService.emitAppointmentBookingEvent(GTM_EVENTS.appointment_booking_step_resources_end);
      }
      this.nextPageEvent.emit({ view: BOOKING_PAGE_VIEWS.SLOTS_VIEW, isUserInteracted });
    } else if (showValidation) {
      // A worker id is set but no matching row exists in this list — the list is rebuilt from the
      // chosen service, so a pre-selected advisor who does not perform it is absent here. Without
      // this branch the method returned having done nothing: the step stayed on screen and the
      // button appeared dead, with no message telling the customer to choose someone.
      this.showWorkerSelectionRequiredError = true;
    }
  }

  previous(): void {
    if (this.isGTMEventSent) {
      this.googleAnalyticsService.emitAppointmentBookingEvent(GTM_EVENTS.appointment_booking_step_resources_end);
    }
    this.previousPageEvent.emit({ view: BOOKING_PAGE_VIEWS.SERVICES_VIEW, isUserInteracted: true });
  }

  setupTemplates(): void {
    this.widgetAnyoneLabelTemplate = this.bookingService.widgetTemplates.find((template: WidgetTemplateModel) => {
      return (template).identifier === WIDGET_TEMPLATES_CONST.WIDGET_ANYONE_LABEL;
    });
    if (this.widgetAnyoneLabelTemplate) {
      this.widgetAnyoneLabelTemplate.is_multi_language = 1;
    }

    this.widgetAnyoneDescTemplate = this.bookingService.widgetTemplates.find((template: WidgetTemplateModel) => {
      return (template).identifier === WIDGET_TEMPLATES_CONST.WIDGET_ANYONE_DESC_LABEL;
    });
    if (this.widgetAnyoneDescTemplate) {
      this.widgetAnyoneDescTemplate.is_multi_language = 1;
    }

    this.widgetResourceHeadingTemplate = this.bookingService.widgetTemplates.find((template: WidgetTemplateModel) => {
      return (template).identifier === WIDGET_TEMPLATES_CONST.WIDGET_RESOURCE_LABEL;
    });
    if (this.widgetResourceHeadingTemplate) {
      this.widgetResourceHeadingTemplate.is_multi_language = 1;
    }
  }
}
